import { EmberPlayMode } from "game-common/models"
import { Container, Graphics, Rectangle } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { Fader } from "./fader"

// The recruit role picker: a small "choose a role" panel that floats above an ember camp member.
// Framed choice cards (Forager / Defender) sit under a title, with a downward pointer aimed at the
// member. Picking a card assigns that role (the server swaps the member into the matching script).
// Styled to match the command wheel's ember-lit look (warm amber rim, dark glass) and drawn entirely
// here — the public API (bind/fade) mirrors CommandWheel so GuiManager can drive it the same way.
//
// It serves two moments, told apart by the `currentRole` bindToEntity is given:
//
//   a HIRE (no current role)    both cards, "Assign this recruit" — the fresh recruit at the fire
//                               holds no job yet, so the choice is between two jobs.
//   a CHANGE (a current role)   ONE card, "Give them new work" — the job the member already does is
//                               not an option, because picking it would be a no-op dressed as a
//                               decision. The panel shrinks to fit the single card.

type PickerChoice = RecruitRole | EmberPlayMode

export type RecruitRole = "forager" | "defender"

// Fade timing — mirrors CommandWheel (~100ms each way).
const FADE_INTERVAL_MS = 5
const FADE_STEP = 0.05

// Ember-lit palette + geometry.
const RIM = 0xf2a94e // warm amber rim / glow
const PANEL_FILL = 0x1b1512
const PANEL_ALPHA = 0.92
const CARD_IDLE_FILL = 0x2a201a
const CARD_HOVER_FILL = 0x33271e
const CARD_IDLE_RIM = 0x6b4f38
const TEXT_COLOR = 0xfff3e0

// What the panel calls itself. A hire is an assignment; a change is a reassignment, and saying so is
// the only cue that the single card on offer is a SWITCH rather than the job they already do.
const HIRE_TITLE = "Assign this recruit"
const CHANGE_TITLE = "New Role"

const PAD = 12
const TITLE_H = 20
const CARD_W = 96
const CARD_H = 104
const CARD_GAP = 12
const TAIL_H = 12
const TAIL_HALF_W = 9
const PANEL_CORNER = 10
const CARD_CORNER = 8

// Overall footprint scale. The stage applies a 1.3× vertical scale, so keep the widget compact
// enough that it still floats clear of the recruit near the top of the screen.
const WIDGET_SCALE = 0.82

// Width is a function of how many cards are on offer (one, at a role change) — height never changes.
const panelWidthFor = (cardCount: number) => cardCount * CARD_W + (cardCount - 1) * CARD_GAP + 2 * PAD
const PANEL_H = TITLE_H + CARD_H + 2 * PAD
// Origin (0,0) is the tail tip (points down at the member); the panel body floats above it.
const PANEL_TOP = -(TAIL_H + PANEL_H)
const CARD_Y = PANEL_TOP + PAD + TITLE_H

interface RoleMeta {
    role: PickerChoice
    emoji: string
    title: string
    // Two short description lines (pre-split to avoid width guesswork).
    desc: [string, string]
    // Role accent, matched to the ember roster's role colors so the whole ember UI reads as one set.
    color: number
}

const ROLE_CARDS: RoleMeta[] = [
    { role: "forager", emoji: "🪓", title: "Forager", desc: ["Gathers wood", "to feed the fire"], color: 0x7be08a },
    { role: "defender", emoji: "🛡️", title: "Defender", desc: ["Guards the camp", "and the Lord"], color: 0xff8a5c },
]

interface CardHandle {
    container: Container
    bg: Graphics
    meta: RoleMeta
    hovered: boolean
}

export interface RolePickerProps {
    // `currentRole` is the role the bound member already held, so the caller can tell a hire from a
    // change (they go out on different commands). Undefined at a hire.
    onSelect?: (role: PickerChoice, currentRole?: PickerChoice) => void
}

export class RolePicker extends Container {
    private onSelect?: (role: PickerChoice, currentRole?: PickerChoice) => void
    private playModePicker = false
    private bg: Graphics
    private cards: CardHandle[] = []
    private title: any

    // The member this picker is bound to (so GuiManager can keep it glued over that NPC and scope
    // the resulting command to it). Undefined while closed.
    private targetEntityId?: string
    // The role that member holds NOW, when it holds one — set only for a "Change Role" order, and
    // what makes this a change rather than a hire. The card for it is not offered.
    private currentRole?: PickerChoice
    // Current panel width: narrower when only one card is on offer. Everything that depends on the
    // body's horizontal extent (glow, rim, hit area, card positions) reads this.
    private panelW: number = panelWidthFor(ROLE_CARDS.length)

    // Whether the downward pointer is drawn. The tail is a claim -- "this panel is about whoever is
    // standing under its tip" -- so it comes off when there is no longer anybody there (see
    // setTailVisible).
    private tailVisible: boolean = true

    // Logical open flag — flips the instant a fade starts so input gating frees immediately (mirrors
    // CommandWheel.isOpen semantics). Only one fader runs at a time.
    private opened: boolean = false
    private fadeInFader: Fader | null = null
    private fadeOutFader: Fader | null = null

    constructor(props: RolePickerProps = {}) {
        super()
        this.onSelect = props.onSelect
        this.scale.set(WIDGET_SCALE)

        // Swallow pointer events over the panel so clicks on the widget never fall through to a world
        // attack (the cards handle their own clicks; this covers the gaps/title too).
        this.interactive = true
        this.hitArea = new Rectangle(this.panelLeft(), PANEL_TOP, this.panelW, PANEL_H + TAIL_H)
        this.on("pointerdown", (event: any) => event.stopPropagation())
        this.on("pointermove", (event: any) => event.stopPropagation())
        this.on("rightclick", (event: any) => event.stopPropagation())
        this.on("contextmenu", (event: any) => {
            event.preventDefault()
            event.stopPropagation()
        })

        this.bg = new Graphics()
        this.addChild(this.bg)
        this.addTitle()

        // Both cards are built once and shown or hidden by layout(); a role change hides one rather
        // than rebuilding the panel, so the hover redraw and the click handlers are wired exactly once.
        this.cards = ROLE_CARDS.map(meta => this.makeCard(meta))
        this.layout()

        this.visible = false
    }

    // Dark "glass" panel with a warm rim, a faint outer glow (a fatter low-alpha copy — cheaper than a
    // blur filter, which matters on low-end GPUs), and a downward pointer whose tip sits at the origin.
    // The tail is drawn as a filled triangle overlapping the body's bottom edge with only its two
    // diagonals stroked, so there's no seam where it meets the body. Nothing below the body is drawn
    // at all once the tail is off, so the panel ends at its own bottom edge.
    private drawBackground() {
        const g = this.bg
        const left = this.panelLeft()
        g.clear()

        // Soft outer glow.
        g.beginFill(RIM, 0.06)
        g.drawRoundedRect(left - 5, PANEL_TOP - 5, this.panelW + 10, PANEL_H + 10, PANEL_CORNER + 5)
        g.endFill()

        // Body.
        g.lineStyle(2, RIM, 0.9)
        g.beginFill(PANEL_FILL, PANEL_ALPHA)
        g.drawRoundedRect(left, PANEL_TOP, this.panelW, PANEL_H, PANEL_CORNER)
        g.endFill()

        if (!this.tailVisible) {
            return
        }

        // Tail fill (base tucked 1px up into the body so the join is hidden).
        const baseY = -TAIL_H - 1
        g.lineStyle(0)
        g.beginFill(PANEL_FILL, PANEL_ALPHA)
        g.moveTo(-TAIL_HALF_W, baseY)
        g.lineTo(0, 0)
        g.lineTo(TAIL_HALF_W, baseY)
        g.closePath()
        g.endFill()

        // Tail rim: stroke only the two visible diagonals (not the hidden base).
        g.lineStyle(2, RIM, 0.9)
        g.moveTo(-TAIL_HALF_W, baseY)
        g.lineTo(0, 0)
        g.lineTo(TAIL_HALF_W, baseY)
    }

    private addTitle() {
        this.title = new TextRenderer().color(TEXT_COLOR).role("caption").bold().align("center").render(HIRE_TITLE)
        this.title.y = PANEL_TOP + PAD - 2
        this.title.alpha = 0.95
        this.addChild(this.title)
    }

    private makeCard(meta: RoleMeta): CardHandle {
        const container = new Container()
        container.y = CARD_Y
        container.interactive = true
        container.cursor = "pointer"
        container.hitArea = new Rectangle(0, 0, CARD_W, CARD_H)

        const bg = new Graphics()
        container.addChild(bg)

        // Emoji (no color/tint so it keeps its own colors).
        const emoji = new TextRenderer().fontSize(34).align("center").render(meta.emoji)
        emoji.x = CARD_W / 2 - emoji.width / 2
        emoji.y = 12
        container.addChild(emoji)

        // Title in the role accent color.
        const title = new TextRenderer().color(meta.color).role("label").bold().align("center").render(meta.title)
        title.x = CARD_W / 2 - title.width / 2
        title.y = 56
        container.addChild(title)

        // Two-line description.
        meta.desc.forEach((line, i) => {
            const t = new TextRenderer().color(TEXT_COLOR).role("compactCaption").align("center").render(line)
            t.x = CARD_W / 2 - t.width / 2
            t.y = 78 + i * 12
            t.alpha = 0.82
            container.addChild(t)
        })

        const handle: CardHandle = { container, bg, meta, hovered: false }
        this.drawCard(handle)

        container.on("pointerover", () => {
            handle.hovered = true
            this.drawCard(handle)
        })
        container.on("pointerout", () => {
            handle.hovered = false
            this.drawCard(handle)
        })
        container.on("pointerdown", (event: any) => {
            event.stopPropagation()
            this.onSelect?.(meta.role, this.currentRole)
        })

        this.addChild(container)
        return handle
    }

    // Draw (or redraw on hover) a card: idle = warm charcoal fill + muted rim; hovered = a soft glow
    // in the role color, a brighter fill, and a full role-color rim.
    private drawCard(handle: CardHandle) {
        const g = handle.bg
        const color = handle.meta.color
        g.clear()
        if (handle.hovered || (this.playModePicker && handle.meta.role === this.currentRole)) {
            g.beginFill(color, 0.16)
            g.drawRoundedRect(-3, -3, CARD_W + 6, CARD_H + 6, CARD_CORNER + 3)
            g.endFill()
            g.lineStyle(2, color, 1)
            g.beginFill(CARD_HOVER_FILL, 0.97)
        } else {
            g.lineStyle(1.5, CARD_IDLE_RIM, 0.7)
            g.beginFill(CARD_IDLE_FILL, 0.9)
        }
        g.drawRoundedRect(0, 0, CARD_W, CARD_H, CARD_CORNER)
        g.endFill()
    }

    private clearHover() {
        this.cards.forEach(card => {
            if (card.hovered) {
                card.hovered = false
                this.drawCard(card)
            }
        })
    }

    // Bind the picker to a single member so GuiManager can keep it positioned over that NPC and scope
    // the chosen role back to it.
    //
    // `currentRole` is the job that member is doing NOW, passed only by a "Change Role" order: its
    // card is dropped from the offer (there is nothing to pick in "keep doing what you are doing")
    // and the panel re-titles and narrows around what is left. Omit it for a hire, where both jobs
    // are on offer.
    public bindPlayMode(targetEntityId: string, currentMode: EmberPlayMode) {
        this.playModePicker = true
        this.replaceCards([
            { role: "pvp", emoji: "⚔️", title: "PVP", desc: ["No restrictions", "Player hits allowed"], color: 0xff8a5c },
            { role: "cooperation", emoji: "🤝", title: "Cooperation", desc: ["Work together", "No player hits"], color: 0x7be08a },
        ])
        this.targetEntityId = targetEntityId
        this.currentRole = currentMode
        this.layout()
    }

    private replaceCards(cards: RoleMeta[]) {
        this.cards.forEach(card => { this.removeChild(card.container); card.container.destroy({ children: true }) })
        this.cards = cards.map(meta => this.makeCard(meta))
    }

    public bindToEntity(targetEntityId: string, currentRole?: RecruitRole) {
        if (this.playModePicker) {
            this.playModePicker = false
            this.replaceCards(ROLE_CARDS)
        }
        this.targetEntityId = targetEntityId
        this.currentRole = currentRole
        this.clearHover()
        this.layout()
    }

    public clearTarget() {
        this.targetEntityId = undefined
        this.currentRole = undefined
    }

    // The left edge of the body, which is centred on the tail tip at the origin.
    private panelLeft(): number {
        return -this.panelW / 2
    }

    // Size the panel to the cards on offer and lay them out centred under the title. Called on every
    // bind, because which cards are on offer is a property of what the picker was opened FOR.
    private layout() {
        const offered = this.cards.map(card => card.meta).filter(meta => this.playModePicker || meta.role !== this.currentRole)
        this.panelW = panelWidthFor(offered.length)

        const firstX = this.panelLeft() + PAD
        let slot = 0
        this.cards.forEach(card => {
            const shown = offered.indexOf(card.meta) >= 0
            this.drawCard(card)
            card.container.visible = shown
            // A hidden card keeps its hit area, so park it out of the way as well as out of sight --
            // an invisible Container still answers pointer events in pixi.
            card.container.interactive = shown
            if (shown) {
                card.container.x = firstX + slot * (CARD_W + CARD_GAP)
                slot += 1
            }
        })

        this.title.text = this.playModePicker ? "Play Mode" : this.currentRole ? CHANGE_TITLE : HIRE_TITLE
        this.title.x = -this.title.width / 2

        this.hitArea = new Rectangle(this.panelLeft(), PANEL_TOP, this.panelW, PANEL_H + (this.tailVisible ? TAIL_H : 0))
        this.drawBackground()
    }

    // Show or hide the downward pointer. A recruit can walk off while the picker is up, and a tail
    // left behind would aim at bare ground -- or worse, at whoever wandered into that spot. Without
    // it the panel is just a panel, which is the honest thing to show. The hit area follows so the
    // widget stops swallowing clicks on a strip of world it no longer covers.
    public setTailVisible(visible: boolean) {
        if (this.tailVisible === visible) {
            return
        }
        this.tailVisible = visible
        this.hitArea = new Rectangle(this.panelLeft(), PANEL_TOP, this.panelW, PANEL_H + (visible ? TAIL_H : 0))
        this.drawBackground()
    }

    public getTargetEntityId(): string | undefined {
        return this.targetEntityId
    }

    // The panel body's footprint, in the PARENT's units (the widget scales itself down). What a
    // caller needs to park the panel somewhere in the HUD rather than over an entity.
    public getPanelWidth(): number {
        return this.panelW * WIDGET_SCALE
    }

    public getPanelHeight(): number {
        return PANEL_H * WIDGET_SCALE
    }

    // How far the widget's origin (the tail TIP) sits below the body's bottom edge. The panel hangs
    // above its own origin, so anything placing the body has to add this back.
    public getTailDrop(): number {
        return TAIL_H * WIDGET_SCALE
    }

    // Park the panel's BODY with its top-left corner at (x, y) in the parent's frame -- how the picker
    // is placed when it belongs to a roster card rather than to an entity on screen.
    public placeBodyTopLeftAt(x: number, y: number) {
        this.position.set(x + this.getPanelWidth() / 2, y + this.getPanelHeight() + this.getTailDrop())
    }

    // Which roles the panel is currently offering, in the order the cards are laid out. One at a role
    // change (the job the member already does is not on offer), both at a hire.
    public getOfferedRoles(): PickerChoice[] {
        return this.cards.filter(card => card.container.visible).map(card => card.meta.role)
    }

    public isOpen(): boolean {
        return this.opened
    }

    // Reveal by fading in, interrupting an in-progress dismiss (mirrors CommandWheel.fadeIn).
    public fadeIn() {
        this.opened = true

        if (this.fadeOutFader) {
            this.fadeOutFader.stop(true)
            this.fadeOutFader = null
        }
        if (this.fadeInFader) {
            return
        }

        if (!this.visible) {
            this.alpha = 0
        }
        this.visible = true

        if (this.alpha >= 1) {
            this.alpha = 1
            return
        }

        this.fadeInFader = new Fader(this, {
            direction: "in",
            minAlpha: 1.0,
            fadeInterval: FADE_INTERVAL_MS,
            fadeAlphaDecrement: FADE_STEP,
            releaseCallback: () => {
                this.fadeInFader = null
                this.alpha = 1
            },
        })
        this.fadeInFader.update()
    }

    // Dismiss by fading out, hiding once transparent (mirrors CommandWheel.fadeOut).
    public fadeOut() {
        this.opened = false
        this.clearHover()

        if (this.fadeInFader) {
            this.fadeInFader.stop(true)
            this.fadeInFader = null
        }
        if (this.fadeOutFader || !this.visible) {
            return
        }

        this.fadeOutFader = new Fader(this, {
            direction: "out",
            minAlpha: 0.0,
            fadeInterval: FADE_INTERVAL_MS,
            fadeAlphaDecrement: FADE_STEP,
            releaseCallback: () => {
                this.fadeOutFader = null
                this.visible = false
                this.alpha = 1.0
            },
        })
        this.fadeOutFader.update()
    }
}
